import * as React from "react";
import * as H from "history";
import { toast } from "react-toastify";
import styled, { css } from "styled-components";
import _ from "lodash";
import { useMappedState } from "redux-react-hook";

import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import Table, { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";
import {
  ISupportTicket,
  SupportTicketStatus,
  useSupportApi
} from "src/modules/support/api/support.api";
import {
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/core/shared/api";
import { Text } from "src/library/components/Text";
import {
  CheckboxStyled,
  Select
} from "src/library/components/Forms";
import SelectedUser from "src/core/users/components/SelectedUser";
import {
  IUser,
  IUserGroup,
  useUserApi
} from "src/core/users/api/users.api";
import SelectedUserGroup from "src/core/users/components/SelectedUserGroup";
import useLocalStorage from "src/library/hooks/useLocalStorage";
import { useCurrentUser } from "src/core/users/hooks";
import { IStore } from "src/core/shared/store";
import { getAdminSupportTicketColumns } from "src/core/shared/components/tables/adminSupportTicketsColumns";

import { SupportTicketActionList } from "../SupportTicketActionList";

interface ViewAdminSupportTicketsTabProps {
  history: H.History;
  terminology: ITerminology;
  dropId?: number;
}

const defaultPagination: PaginatedParams<ISupportTicket> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "priority", "DESC" ] ]
};

type QuickFilter = "currentUser" | "assignedUser" | "userGroup" | "all";

const quickFilterOptions: Array<{text: string; value: QuickFilter }> = [
  {
    text: "Assigned To Me",
    value: "currentUser"
  },
  {
    text: "Assigned User",
    value: "assignedUser"
  },
  {
    text: "User Group",
    value: "userGroup"
  },
  {
    text: "All",
    value: "all"
  }
];

interface QuickFilters extends Partial<Pick<ISupportTicket, "assignedUserId" | "userGroupId">> {
  currentUserId?: number;
  all?: null;
}

/**
 * Support Tickets overview tab
*/
const ViewAdminSupportTicketsTab: React.ReactFCWithChildren<ViewAdminSupportTicketsTabProps> = ({ terminology, dropId }) => {
  const localStorageKeys = React.useMemo(() => ({
    SELECTED_FILTER: dropId ? "drop-support-tickets_selected-filter" : "support-tickets_selected-filter",
    QUICK_FILTER: dropId ? "drop-support-tickets_quick-filter" : "support-tickets_quick-filter"
  }), [ dropId ]);

  const TableRef = React.useRef<TableRefProps>(null);
  const [ isLoading, setIsLoading ] = React.useState(false);
  const [ supportTickets, setSupportTickets ] = React.useState<PaginatedData<ISupportTicket>>();
  const [ listSupportTicketsRes, listSupportTicketsReq ] = useSupportApi("LIST_SUPPORT_TICKETS");
  const [ getUserResponse, getUser ] = useUserApi("GET_USER");
  const [ getUserGroupResponse, getUserGroup ] = useUserApi("GET_USER_GROUP");
  const [ selectedQuickFilter, setSelectedQuickFilter ] = useLocalStorage<QuickFilter>(localStorageKeys.SELECTED_FILTER, dropId ? "all" : "currentUser");
  const [ quickFilters, setQuickFilters ] = useLocalStorage(localStorageKeys.QUICK_FILTER, "{}");
  const [ tableFilters, setTableFilters ] = React.useState<FilterParams<ISupportTicket>>({});
  const [ assignedUser, setAssignedUser ] = React.useState<IUser | null>(null);
  const [ selectedUserGroup, setSelectedUserGroup ] = React.useState<IUserGroup | null>(null);
  const [ { currentUser, currentRole } ] = useCurrentUser();
  const [ showResolvedTickets, setShowResolvedTickets ] = React.useState<boolean | null>(null);
  const { supportTicketTypes } = useMappedState((store: IStore) => ({ supportTicketTypes: store.global.settings && store.global.settings.supportTicketTypes }));

  // quick filter persisted in local storage
  const quickFilter: QuickFilters = React.useMemo(() => {
    try {
      return JSON.parse(quickFilters || "{}");
    } catch (error) {
      return {};
    }
  }, [ quickFilters ]);

  React.useEffect(() => {
    if (quickFilter?.assignedUserId) {
      getUser({ id: quickFilter.assignedUserId });
    }

    if (quickFilter?.userGroupId) {
      getUserGroup({ id: quickFilter.userGroupId });
    }
  }, [
    quickFilter,
    getUser,
    getUserGroup
  ]);

  // update assignedUser if request returns user
  React.useEffect(() => {
    if (getUserResponse.data) {
      setAssignedUser(getUserResponse.data);
    }
  }, [ getUserResponse ]);

  // update selectedUserGroup if request returns user
  React.useEffect(() => {
    if (getUserGroupResponse.data) {
      setSelectedUserGroup(getUserGroupResponse.data);
    }
  }, [ getUserGroupResponse ]);

  // used to manage local state for quick filters and update table data
  const applyQuickFilter = React.useCallback((type: QuickFilter, data?: IUser | IUserGroup | null) => {
    setIsLoading(true);
    let filterKey: keyof QuickFilters = "all";

    // update local state for displaying selected value
    if (type === "assignedUser" || type === "currentUser") {
      if (type === "assignedUser") {
        setAssignedUser(data as IUser);
      }

      filterKey = "assignedUserId";
    } else if (type === "userGroup") {
      setSelectedUserGroup(data as IUserGroup);
      filterKey = "userGroupId";
    } else if (type === "all") {
      // reset data and return
      setAssignedUser(null);
      setSelectedUserGroup(null);
    }

    // set filter value based on type
    const filterValue = type === "currentUser" ? currentUser?.id : data?.id || null;

    try {
      // update quick filters for table filters to use
      setQuickFilters(JSON.stringify({ [ filterKey ]: filterValue }));
    } catch (error) {
      // soft fail if local storage fails
    }

    if (type === "all") {
      // ensure tableFilters don't include assignedUser or userGroup filters
      // and refetch table data with no quick filter applied
      listSupportTicketsReq({
        filters: { ..._.omit(tableFilters, [ "assignedUserId", "userGroupId" ]) },
        pagination: defaultPagination
      });
    } else {
      // refetch table data with relevant filter - inc tableFilters
      listSupportTicketsReq({
        filters: {
          ...tableFilters,
          [ filterKey ]: filterValue
        },
        pagination: defaultPagination
      });
    }
  }, [
    currentUser?.id,
    listSupportTicketsReq,
    setQuickFilters,
    tableFilters
  ]);

  // Fetches table data
  const onFetchData = React.useCallback((params: {filters: FilterParams<ISupportTicket>, pagination: PaginatedParams<ISupportTicket>}) => {
    const filters = params.filters;
    const pagination = params.pagination;
    // mark intial state of filters.status before manipulating filters below
    const filterStatus = filters.status;

    // Set loading to true
    setIsLoading(true);
    // keep table filters state accessible local to this component
    setTableFilters(filters);
    // reset filters.status as will update below based on state
    filters.status = undefined;

    if (Object.keys(quickFilter).length === 0 && selectedQuickFilter === "currentUser" && currentUser) {
      // we've mounted and setting local storage for first time
      // set relevant values and filter
      setAssignedUser(currentUser);
      setQuickFilters(JSON.stringify({ assignedUserId: currentUser.id }));
      filters.assignedUserId = currentUser.id;
    }

    // sanatise quick filter "all" option
    if (Object.keys(quickFilter).includes("all")) {
      delete quickFilter.all;
    }

    if (!showResolvedTickets && (
      !filters.id &&
      !filters.status &&
      !filters?.order?.customerReference &&
      !filters?.branch
    )) {
      filters.status = { $notIn: [ SupportTicketStatus.AdminResolved, SupportTicketStatus.CustomerResolved ] };
    }

    // handle provided filter.status before transformation
    if (filterStatus) {
      if (filterStatus === "all_resolved") {
        // force resolved status if initial status filter desires only resolved tickets
        filters.status = { $in: [ SupportTicketStatus.AdminResolved, SupportTicketStatus.CustomerResolved ] };
      } else if (typeof filterStatus === "string") {
        // simply apply relevant filter
        filters.status = filterStatus;
      }
    }

    if (dropId) {
      filters.branchId = dropId;
    }

    // Fetch support tickets
    listSupportTicketsReq({
      filters: {
        ...quickFilter,
        ...filters
      },
      pagination
    });
  }, [
    currentUser,
    dropId,
    listSupportTicketsReq,
    quickFilter,
    selectedQuickFilter,
    setQuickFilters,
    showResolvedTickets
  ]);

  // listSupportTicketsRes change
  React.useEffect(() => {
    // If an error occurs show a toast
    if (listSupportTicketsRes.error) {
      setIsLoading(false);
      toast.error(listSupportTicketsRes.error.message);
    }

    // On success
    if (listSupportTicketsRes.data) {
      setIsLoading(false);
      setSupportTickets(listSupportTicketsRes.data);
    }
  }, [ listSupportTicketsRes ]);

  const columns = React.useMemo(() => {
    return getAdminSupportTicketColumns({
      actionButton: info => {
        return (
          <SupportTicketActionList
            ticketId={info.row.original.id}
            context={dropId ? "drop" : undefined}
          />
        );
      },
      ExcludedColumns: dropId ? [ "branch" ] : undefined,
      terminology,
      currentRole,
      supportTicketTypes
    });
  }, [
    dropId,
    terminology,
    currentRole,
    supportTicketTypes
  ]);

  return (
    <SupportTicketTabWrapper>
      <div className="quick-filter">
        <div className="header">
          <Text weight="bold">
            Quick Filters
          </Text>
        </div>

        <div className="top-filters">
          <Select
            className="quick-filter-select"
            as="select"
            name="quick-filter"
            label="quick filter"
            autoHideLabels
            handleChange={e => {
              setSelectedQuickFilter(e.target.value);
              applyQuickFilter(e.target.value);
            }}
            options={quickFilterOptions}
            value={selectedQuickFilter}
          />

          <div className="show-resolved-tickets">
            <CheckboxStyled
              checked={!!showResolvedTickets}
              onChange={e => setShowResolvedTickets(e.target.checked)}
            />

            <Text className="checkbox-label">
              Show Resolved Tickets
            </Text>
          </div>
        </div>

        {selectedQuickFilter === "assignedUser" && (
          <div className="sub-filter">
            <div className="sub-filter-label">
              <Text>
                Selected User:
              </Text>
            </div>

            <SelectedUser
              user={assignedUser}
              handleUpdate={data => applyQuickFilter("assignedUser", data)}
              terminology={terminology}
            />
          </div>
        )}

        {selectedQuickFilter === "userGroup" && (
          <div className="sub-filter user-group">
            <div className="sub-filter-label">
              <Text>
                Selected Group:
              </Text>
            </div>

            <SelectedUserGroup
              userGroup={selectedUserGroup}
              handleUpdate={data => applyQuickFilter("userGroup", data)}
            />
          </div>
        )}
      </div>

      <Table
        ref={TableRef}
        loading={isLoading}
        tableOf="Support Tickets"
        defaultPagination={defaultPagination}
        data={supportTickets ? supportTickets.result : []}
        onFetchData={onFetchData}
        pageCount={supportTickets ? supportTickets.pages : 0}
        rowCount={supportTickets ? supportTickets.count : 0}
        columns={columns}
      />
    </SupportTicketTabWrapper>
  );
};

const SupportTicketTabWrapper = styled.div`
  ${({ theme }) => css`
    .quick-filter {
      .header {
        margin-bottom: ${theme.spacing.margins.md};
      }

      .top-filters {
        display: flex;
        align-items: center;

        .show-resolved-tickets {
          margin-left: ${theme.spacing.margins.xl};
          display: flex;
          align-items: center;

          .checkbox-label {
            margin-left: ${theme.spacing.margins.md};
          }
        }
      }

      .sub-filter {
        margin-top: ${theme.spacing.margins.md};
        display: flex;
        
        span {
          margin-right: ${theme.spacing.margins.md};
        }
      }

      .selected-user {
        min-width: 300px;
        border: solid 1px ${theme.colors.lightGrey};
        padding: 0 ${theme.spacing.padding.md};
        height: 36px;
      }

      .user-search-component {
        border: solid 1px ${theme.colors.lightGrey};
        max-width: 300px;
      }

      .sub-filter-label {
        display: flex;
        align-items: center;
      } 

      .user-group {
        select {
          width: 293px;
        }
      } 
    }
  `}
`;

export default ViewAdminSupportTicketsTab;
